// MARK:- Newsletter popup controller — global event-delegated, no wrapping
// custom element. Replaces the legacy `openToast('newsletter')` autoplay in
// src/js/theme.js (which now silently no-ops because the new section dropped
// id="newsletter"). Does NOT extend or touch the legacy code.
//
// Loaded as an ES module from sections/newsletter-toast.liquid:
//
//   <script src="{{ 'component-newsletter-toast.js' | asset_url }}" type="module"></script>
//
// `type="module"` deduplicates by URL so re-renders during customizer edits
// only execute the script once per page.
//
// Markup contract (sections/newsletter-toast.liquid → snippets/newsletter-toast.liquid):
//
//   <div class="toast toast--newsletter"
//        data-as-newsletter-toast
//        data-delay="8"
//        data-dismiss-days="30"
//        data-dev-mode="false">
//     ...
//     <button class="toast__close">…</button>
//     <form id="toast-newsletter-form">…</form>
//   </div>
//
// Behavior:
// - On DOMContentLoaded, read data-delay, data-dismiss-days, and data-dev-mode
//   from the toast.
// - If the page has a [data-as-hide-newsletter-popup] element (a signup section
//   opting out — see sections/newsletter-signup.liquid), do nothing.
// - If data-dev-mode="true", skip the suppression check entirely (popup shows
//   every page load — for merchant content editing).
// - Otherwise, if localStorage has a dismiss timestamp within dismiss_days, do
//   nothing.
// - Otherwise, after `delay` seconds, add .is-open to reveal it (CSS owns the
//   transition).
// - Document-delegated click on .toast--newsletter .toast__close → close + write
//   timestamp.
// - Document-delegated submit on #toast-newsletter-form → write timestamp
//   (best-effort; Shopify {% form 'customer' %} server-redirects so we won't see
//   a successful response client-side).
//
// Note: legacy handleToasts() at src/js/theme.js:296 binds .toast__close clicks
// globally to fadeOut + remove .is-open. That co-runs harmlessly with our
// delegated handler — both close, only ours writes the timestamp.

const STORAGE_KEY = 'asever:newsletter-dismissed-at';
const DAY_MS = 86_400_000;

let initialized = false;

function getToast() {
  return document.querySelector('[data-as-newsletter-toast]');
}

function isSuppressed(dismissDays) {
  if (dismissDays <= 0) return false;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return false;
  const ts = parseInt(stored, 10);
  if (!Number.isFinite(ts)) return false;
  return Date.now() - ts < dismissDays * DAY_MS;
}

function recordDismiss() {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch (e) {
    // localStorage unavailable (private mode quotas, disabled storage) — fall
    // back to per-session suppression by leaving .is-open removed; nothing else
    // to do.
  }
}

// MARK:- toggle `inert` alongside .is-open so the offscreen toast stays out of tab order/AT until shown
function open(toast) {
  toast.removeAttribute('inert');
  toast.classList.add('is-open');
}

function close(toast) {
  toast.setAttribute('inert', '');
  toast.classList.remove('is-open');
}

function init() {
  if (initialized) return;
  initialized = true;

  const toast = getToast();
  if (!toast) return;

  // MARK:- A signup section on the page can hide the popup to avoid stacked forms
  if (document.querySelector('[data-as-hide-newsletter-popup]')) return;

  const delaySeconds = parseInt(toast.dataset.delay, 10) || 0;
  const dismissDays = parseInt(toast.dataset.dismissDays, 10) || 0;
  const devMode = toast.dataset.devMode === 'true';

  if (!devMode && isSuppressed(dismissDays)) return;

  setTimeout(() => open(toast), delaySeconds * 1000);

  document.addEventListener('click', (event) => {
    const closeBtn = event.target.closest('.toast--newsletter .toast__close');
    if (!closeBtn) return;
    const t = closeBtn.closest('.toast--newsletter');
    if (t) close(t);
    recordDismiss();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form && form.id === 'toast-newsletter-form') {
      recordDismiss();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
